import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, User, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API, getGymData } from '../lib/api';

const AIChatWidget = ({ gymName = 'Fitxero' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const toggleChat = () => setIsOpen(!isOpen);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async text => {
    const messageText = typeof text === 'string' ? text : inputValue;
    if (!messageText.trim()) {
      return;
    }

    // Add user message
    const userMessage = { id: Date.now(), text: messageText, sender: 'user' };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsTyping(true);

    try {
      // Map to the format backend expects: role and content
      const apiMessages = newMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));

      const gymData = getGymData();
      const gymId = gymData?.profile?.id || gymData?.id || 'public';

      // Artificial delay for premium "thinking" feel before revealing response
      const [{ data }] = await Promise.all([
        axios.post(`${API}/chat`, {
          gym_id: gymId,
          messages: apiMessages,
        }),
        new Promise(resolve => setTimeout(resolve, 800)),
      ]);

      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          text: data.response || 'Small issue, try again 😅',
          sender: 'ai',
        },
      ]);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Chat Error:', error?.message);
      }
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          text: 'Small issue, try again 😅',
          sender: 'ai',
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const quickButtons = ['Pricing', 'Free Trial', 'Timings'];

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end">
      {/* Chat Window */}
      <div
        className={`mb-4 w-[calc(100vw-2rem)] sm:w-[380px] bg-[#0c0e12] border border-gray-800/80 shadow-[0_8px_30px_rgb(0,0,0,0.4)] rounded-2xl overflow-hidden transition-all duration-300 origin-bottom-right flex flex-col ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-10 pointer-events-none absolute'
        }`}
        style={{ maxHeight: 'calc(100vh - 6rem)' }}
      >
        {/* Header */}
        <div className="bg-[#12141a] border-b border-gray-800/60 p-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 shadow-inner">
                <Bot className="w-5 h-5 text-indigo-400" />
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-[#12141a] rounded-full"></span>
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-1.5 text-sm">
                {gymName} AI <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
              </h3>
              <p className="text-emerald-400 text-xs font-medium">Online • Responds instantly</p>
            </div>
          </div>
          <button
            onClick={toggleChat}
            className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-800/50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message Area */}
        <div className="h-[400px] sm:h-[450px] p-4 overflow-y-auto bg-[#0a0c10] flex flex-col gap-5 custom-scrollbar">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 text-gray-400 animate-in fade-in duration-500">
              <div className="w-16 h-16 bg-gray-800/40 rounded-full flex items-center justify-center mb-2 shadow-inner border border-gray-800/60">
                <span className="text-3xl">👋</span>
              </div>
              <div>
                <p className="text-gray-200 font-medium mb-1">Hi there!</p>
                <p className="text-sm">Ask anything about this gym.</p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {quickButtons.map(btn => (
                  <button
                    key={btn}
                    onClick={() => handleSend(btn)}
                    className="px-3.5 py-1.5 bg-[#1a1d24] hover:bg-gray-800 text-xs font-medium text-gray-300 rounded-full transition-colors border border-gray-700/50 hover:border-gray-500 shadow-sm"
                  >
                    {btn}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`flex gap-2.5 max-w-[90%] sm:max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-auto shadow-sm">
                      {msg.sender === 'user' ? (
                        <div className="w-full h-full bg-indigo-600 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      ) : (
                        <div className="w-full h-full bg-[#1a1d24] border border-gray-800/80 rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 text-indigo-400" />
                        </div>
                      )}
                    </div>
                    <div
                      className={`p-3.5 rounded-2xl text-[14px] leading-relaxed shadow-sm whitespace-pre-wrap ${
                        msg.sender === 'user'
                          ? 'bg-indigo-600 text-white rounded-br-sm'
                          : 'bg-[#1a1d24] text-gray-200 rounded-bl-sm border border-gray-800/60'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex w-full justify-start animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="flex gap-2.5 max-w-[85%] flex-row">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-auto shadow-sm">
                      <div className="w-full h-full bg-[#1a1d24] border border-gray-800/80 rounded-full flex items-center justify-center">
                        <Bot className="w-4 h-4 text-indigo-400" />
                      </div>
                    </div>
                    <div className="p-4 bg-[#1a1d24] rounded-2xl rounded-bl-sm border border-gray-800/60 shadow-sm flex items-center gap-1.5 h-[44px]">
                      <span
                        className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      ></span>
                      <span
                        className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      ></span>
                      <span
                        className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      ></span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-3.5 bg-[#12141a] border-t border-gray-800/60 shrink-0">
          <form
            onSubmit={e => {
              e.preventDefault();
              handleSend();
            }}
            className="relative flex items-center"
          >
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="Message your AI Assistant..."
              className="w-full bg-[#0a0c10] border border-gray-800/80 text-white rounded-full py-3.5 pl-4 pr-12 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all text-[14px] shadow-inner"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isTyping}
              className="absolute right-1.5 p-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded-full transition-colors flex items-center justify-center shadow-sm"
            >
              <Send className="w-4 h-4 ml-0.5" />
            </button>
          </form>
          <div className="text-center mt-2.5">
            <span className="text-[10px] text-gray-500/80 font-medium tracking-wide">
              POWERED BY FITXERO AI
            </span>
          </div>
        </div>
      </div>

      {/* Floating Button */}
      <button
        onClick={toggleChat}
        className={`w-14 h-14 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-[0_0_20px_rgba(79,70,229,0.3)] flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95 ${
          isOpen
            ? 'rotate-90 scale-0 opacity-0 absolute pointer-events-none'
            : 'rotate-0 scale-100 opacity-100'
        }`}
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    </div>
  );
};

export default AIChatWidget;
